import { Icons, Tag, Typography } from "@flash-tecnologia/hros-web-ui-v2";
import * as Popover from "@radix-ui/react-popover";
import {
  Children,
  createContext,
  isValidElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "styled-components";
import { Option, SelectOption } from "./components/Option/option.index";
import { OptionsWrapper } from "./components/OptionsWrapper/options-wrapper";
import { TriggerButton } from "./components/TriggerButton/trigger-button";
import { Value } from "./components/Value/value";
import * as SC from "./dropdown.styles";
import { useVisibleTags } from "./hooks/useVisibleTags";

export enum Sizes {
  Lg = "lg",
  Md = "md",
  Sm = "sm",
}

export enum DropdownModes {
  SingleSelect = "single-select",
  MultiSelect = "multi-select",
}

const getTriggerIconSize = (size: Sizes) => {
  return {
    [Sizes.Lg]: 24,
    [Sizes.Md]: 16,
    [Sizes.Sm]: 16,
  }[size ?? Sizes.Md];
};

export interface DropdownRootContextData {
  selectedIds: Set<string>;
  selectedOptions?: SelectOption[];
  toggleSelection: (checked: boolean, selectedOption: SelectOption) => void;
}

const DropdownRootContext = createContext({} as DropdownRootContextData);

type DropDownRootProps = (
  | {
      mode: DropdownModes.SingleSelect;
      selectedOption?: SelectOption | null;
      selectedOptions?: never;
    }
  | {
      mode: DropdownModes.MultiSelect;
      selectedOption?: never;
      selectedOptions?: SelectOption[];
    }
) & {
  error?: boolean;
  children?: ReactNode;
  helperText?: string;
  label?: string;
  size: Sizes;
  disabled?: boolean;
  onBlur?: () => void;
};

function useClosestPortal(ref: React.RefObject<HTMLElement>) {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  const [zIndex, setZIndex] = useState<number>(1000);

  useEffect(() => {
    if (!ref.current) return;

    const parent = ref.current.parentElement ?? document.body;
    setPortalContainer(document.body);

    const parentZIndex = window.getComputedStyle(parent).zIndex;
    setZIndex(parentZIndex !== "auto" ? parseInt(parentZIndex, 10) + 1 : 1000);
  }, [ref]);

  return { portalContainer, zIndex };
}

export const DropdownRoot = ({
  error = false,
  helperText,
  label,
  children,
  size,
  mode,
  disabled = false,
  selectedOption,
  selectedOptions,
  onBlur,
}: DropDownRootProps) => {
  const [selected, setSelected] = useState<SelectOption[]>(
    mode === DropdownModes.MultiSelect
      ? selectedOptions ?? []
      : mode === DropdownModes.SingleSelect
      ? selectedOption
        ? [selectedOption]
        : []
      : []
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [expanded, setExpanded] = useState<boolean>(false);

  const { containerRef, tagRefs, triggerButtonRef, plusRef, visibleCount } =
    useVisibleTags(selected);

  tagRefs.current = new Array(selected.length).fill(null);

  useEffect(() => {
    setSelectedIds(new Set(selected?.map(({ id }) => id)));
  }, [selected]);

  const triggerRef = useRef<HTMLDivElement>(null);
  const { portalContainer, zIndex } = useClosestPortal(triggerRef);

  const theme = useTheme();

  let ValueChild: React.ReactNode;
  const OtherChildren: React.ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      switch (child.type) {
        case Value:
          ValueChild = child;
          break;
        default:
          OtherChildren.push(child);
          break;
      }
    }
  });

  const toggleSelection = (checked: boolean, option: SelectOption) => {
    switch (mode) {
      case DropdownModes.SingleSelect:
        setSelected(() => {
          return checked ? [option] : [];
        });
        break;
      case DropdownModes.MultiSelect:
        setSelected((prev) => {
          if (checked) {
            return prev && [...prev, option];
          }
          return prev?.filter(({ id: currentId }) => currentId !== option.id);
        });
        break;
      default:
        break;
    }
  };

  if (mode === DropdownModes.SingleSelect && selectedOptions !== undefined) {
    throw new Error(
      "Error: selectedOptions should not be present in SingleSelect mode."
    );
  }

  if (mode === DropdownModes.MultiSelect && selectedOption !== undefined) {
    throw new Error(
      "Error: selectedOption should not be present in MultiSelect mode."
    );
  }

  const isPlusTagVisible = selected?.length > 1 && visibleCount > 1;

  useEffect(() => {
    setSelected(
      mode === DropdownModes.MultiSelect
        ? selectedOptions ?? []
        : mode === DropdownModes.SingleSelect
        ? selectedOption
          ? [selectedOption]
          : []
        : []
    );
  }, [selectedOption, selectedOptions, mode]);

  return (
    <div ref={triggerRef}>
      <DropdownRootContext.Provider
        value={{ selectedIds, selectedOptions, toggleSelection }}
      >
        <Popover.Root
          onOpenChange={(open) => {
            setExpanded(open);
            if (!open) onBlur?.();
          }}
        >
          <Popover.Trigger asChild>
            <TriggerButton
              ref={triggerButtonRef}
              size={size}
              error={error}
              disabled={disabled}
            >
              <SC.LabelWithSelectedOptionsWrapper>
                <Typography
                  variant={
                    selectedOptions?.length || selectedOption
                      ? "caption"
                      : "body3"
                  }
                  weight={600}
                  color={disabled ? "neutral.60" : "neutral.40"}
                  style={
                    selectedOptions?.length || selectedOption
                      ? { fontSize: "0.75rem", lineHeight: "18px" }
                      : { fontSize: "0.875rem", lineHeight: "20px" }
                  }
                >
                  {label}
                </Typography>
                <SC.SelectedOptionsWrapper ref={containerRef}>
                  {selected?.slice(0, visibleCount).map(({ id, label }, i) => {
                    return {
                      [DropdownModes.MultiSelect]: (
                        <Tag
                          key={id}
                          ref={(el) => (tagRefs.current[i] = el)}
                          as="span"
                          variant="pink"
                          style={SC.localStyles.selectedOptionsTag}
                        >
                          {label}
                        </Tag>
                      ),
                      [DropdownModes.SingleSelect]: ValueChild || (
                        <Typography.Body4
                          color="neutral.30"
                          weight={600}
                          style={SC.localStyles.selectedOptionLabel}
                        >
                          {label}
                        </Typography.Body4>
                      ),
                    }[mode];
                  })}
                  {isPlusTagVisible && (
                    <Tag
                      ref={plusRef}
                      as="span"
                      variant="pink"
                      style={SC.localStyles.plusOptionTag(
                        selected.length > visibleCount
                      )}
                    >
                      +{selected.length - visibleCount}
                    </Tag>
                  )}
                </SC.SelectedOptionsWrapper>
              </SC.LabelWithSelectedOptionsWrapper>
              <Icons
                name={expanded ? "IconChevronUp" : "IconChevronDown"}
                color={theme.colors.neutral[50]}
                fill="transparent"
                size={getTriggerIconSize(size)}
              />
            </TriggerButton>
          </Popover.Trigger>
          {portalContainer && (
            <Popover.Portal container={portalContainer}>
              <Popover.Content asChild sideOffset={8} hideWhenDetached>
                <SC.PopoverContentWrapper zIndex={zIndex}>
                  {OtherChildren}
                </SC.PopoverContentWrapper>
              </Popover.Content>
            </Popover.Portal>
          )}
        </Popover.Root>
        {helperText && (
          <SC.HelperTextWrapper>
            <Icons
              name="IconInfoCircle"
              size={16}
              color={
                error
                  ? theme.colors.feedback.error[50]
                  : theme.colors.neutral[50]
              }
            />
            <Typography.Caption color="neutral.40">
              {helperText}
            </Typography.Caption>
          </SC.HelperTextWrapper>
        )}
      </DropdownRootContext.Provider>
    </div>
  );
};

export const useDropdownContext = () => {
  const context = useContext(DropdownRootContext);

  if (!context) {
    throw new Error("useDropdownContext must be used within a RootProvider");
  }

  return context;
};

export const Dropdown = {
  Root: DropdownRoot,
  Option,
  OptionsWrapper,
  Value,
};
